import React, { memo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import ListWithPopup from '@atlassian/list-with-popup/ListWithPopup.tsx';
import { TagItem, TagItemPopupContentWrapper } from '@atlassian/list-with-popup/TagItem.tsx';
import { memoizeGetAutoGeneratedColorForLabels } from '@atlassian/option-color-picker/getAutoGeneratedColorForLabels';
import { getColorMetadata } from '@atlassian/option-color-picker/getColorMetadata';

import type { JiraLabelsField_Platform$key } from './__generated__/JiraLabelsField_Platform.graphql';

const messages = defineMessages({
	showMoreTooltip: {
		id: 'ai-first-create.jiraLabelsField.showMoreTooltip',
		description: 'Tooltip for the show more labels button',
		defaultMessage: 'Show more labels',
	},
});

const styles = cssMap({
	readOnlyViewContainer: {
		paddingLeft: token('space.100'),
		display: 'flex',
		alignItems: 'center',
		height: '40px',
		width: '100%',
	},
});

export type Props = {
	field: JiraLabelsField_Platform$key;
	fieldValue: unknown;
	onChange?: (newValue: string[] | null) => void;
};

/**
 * `JiraLabelsField` is a memoized React component that renders a Jira labels field in read-only mode.
 *
 * Displays all labels as simple tags in a wrapping layout.
 *
 * @param field - The field metadata (not used).
 * @param fieldValue - The currently selected labels (array of strings).
 * @param onChange - Callback invoked when labels change (not used in read-only mode).
 * @returns A JSX element representing the labels field, or null if no labels exist.
 */
export const JiraLabelsField: React.MemoExoticComponent<
	(props: Props) => React.JSX.Element | null
> = memo(({ field, fieldValue, onChange: _onChange }: Props): React.JSX.Element | null => {
	const data = useFragment<JiraLabelsField_Platform$key>(
		graphql`
			fragment JiraLabelsField_Platform on JiraLabelsField {
				id
			}
		`,
		field,
	);

	const { formatMessage } = useIntl();

	const labels = Array.isArray(fieldValue)
		? fieldValue.filter((label) => typeof label === 'string')
		: [];

	if (labels.length === 0) {
		return null;
	}

	// TODO - remove this fake condition when adding inline edit
	// Adding this just so I can keep the relay fragment happy
	if (!data.id) {
		return null;
	}

	return (
		<Box xcss={styles.readOnlyViewContainer}>
			<ListWithPopup
				items={labels.map((label) => {
					return {
						name: label,
						key: label,
						isColored: true,
						color: getColorMetadata(memoizeGetAutoGeneratedColorForLabels(label)).backgroundColor,
					};
				})}
				ItemComponent={TagItem}
				PopupContentWrapper={TagItemPopupContentWrapper}
				showMoreTooltip={formatMessage(messages.showMoreTooltip)}
				isHoverPopoverEnabled
			/>
		</Box>
	);
});
